import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, IconButton, List, Stack, styled, Typography } from '@mui/material'
import FocusTrap from '@mui/material/Unstable_TrapFocus'
import type { FC, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { COLORS, ICONS, IconFixedSize, unit } from '@hcr/ui'

import { LINK_CONFIG } from '../../../../../configs'
import { useLastPath } from '../../../../../contexts'
import { useLocale } from '../../../../../hooks'
import { Path } from '../../../../../models'
import { to } from '../../../../../utils'
import { LayoutBase, Metadata } from '../../../../common'
import { Link } from '../link'

interface LayoutContextMenuProps {
  cta: ReactNode
  list: ReactNode
}

const TITLE_ID = 'context-menu-title'

export const LayoutContextMenu: FC<LayoutContextMenuProps> = ({ cta, list }) => {
  const { t } = useTranslation()
  const lastPath = useLastPath()
  const locale = useLocale()
  const navigate = useNavigate()

  return (
    <LayoutBase>
      <Metadata title={t('page-title.context-menu')} />
      <FocusTrap open>
        <Container role='dialog' aria-modal='true' aria-labelledby={TITLE_ID}>
          <Header as='header'>
            <Stack paddingX={unit(3)} paddingY={unit(2)}>
              <IconButton
                onClick={() => navigate(lastPath.root)}
                aria-label={t('context-menu.close')}
                sx={{
                  height: unit(12),
                  width: unit(12),
                  alignSelf: 'end',
                  fontSize: unit(6),
                }}
              >
                <IconFixedSize icon={ICONS.farXmark} aria-hidden='true' />
              </IconButton>
            </Stack>
            <Typography id={TITLE_ID} component='h1' variant='uppercaseS'>
              {t('context-menu.your-bookings')}
            </Typography>
          </Header>
          <Box component='main'>
            <List>{list}</List>
          </Box>
          <Footer as='footer'>
            <Link
              description={t('context-menu.explore-and-book-services')}
              icon={<FontAwesomeIcon icon={ICONS.farArrowRight} />}
              title={t('context-menu.browse-destinations')}
              to={to(Path.ContextMenuDestinations)}
            />
            <Link
              description={t('context-menu.find-your-next-holiday')}
              icon={<FontAwesomeIcon icon={ICONS.farArrowUpRightFromSquare} />}
              isExternal
              title={t('context-menu.book-a-vacation')}
              to={LINK_CONFIG.store[locale.id]}
            />
            <Cta>{cta}</Cta>
          </Footer>
        </Container>
      </FocusTrap>
    </LayoutBase>
  )
}

const Container = styled(Box)`
  min-height: 100vh;
  background-color: ${COLORS.vaalea['60%']};
`

const Header = styled(Box)`
  padding: 0 0 ${unit(3.5)} ${unit(7)};
`

const Footer = styled(Box)`
  margin-top: ${unit(9)};
  background-color: ${COLORS.vaalea['60%']};
`

const Cta = styled(Box)`
  padding-inline: ${unit(7)};
  padding-bottom: ${unit(9)};
  padding-top: ${unit(19.5)};
`
